import {
  BMComponentsType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemDocument16 from "@traveloka/icon-kit-web/react/IcSystemDocument16";
import IcSystemUpload from "@traveloka/icon-kit-web/react/IcSystemUpload";
import { ChangeEvent, createElement, useRef, useState } from "react";
import { Platform, Pressable, StyleSheet } from "react-native";
import BMView from "../../../components/View/BMView";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";

type Props = {
  onChange: (file: File | undefined) => void;
  placeholder?: string;
  label?: string;
  caption?: string;
  errorMessage?: string;
  isAPISuccess?: boolean;
  isLoading?: boolean;
  testID?: string;
};
export default function DocumentField(props: Props) {
  const {
    label,
    onChange,
    caption,
    errorMessage,
    isLoading,
    isAPISuccess = true, // Default to true if not using API
    placeholder,
    testID,
  } = props;
  const [file, setFile] = useState<File>();

  const { BMComponents } = useBMTheme();
  const styles = createStyles(BMComponents);

  const inputRef = useRef<HTMLInputElement>(null);
  const { CorporateCommon: cr } = useMobileContentResource();

  const isSuccess = isAPISuccess && !!file;

  function shownText() {
    if (isLoading) {
      return cr.uploading;
    } else if (isSuccess) {
      return file.name;
    }
    return "";
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.[0]) {
      onChange(e.target.files[0]);
      setFile(e.target.files[0]);
    }
  }

  function handleDelete() {
    if (inputRef.current) {
      inputRef.current.value = "";

      setFile(undefined);
    }
    onChange(undefined);
  }

  return (
    <BMView>
      {!!label && (
        <BMText variant="uiNormal" ink="BMStyleTextInkPrimary">
          {label}
        </BMText>
      )}
      <Pressable onPress={() => inputRef.current?.click()} testID={testID}>
        <BMView
          style={[styles.documentContainer, label ? styles.separator : null]}
          align="center"
          spacing="sm"
          row
        >
          <BMView style={styles.icon}>
            {isSuccess ? (
              <IcSystemDocument16
                color={BMComponents.BMStyleIconInkPrimary}
                accentColor={BMComponents.BMStyleIconInkPrimary}
              />
            ) : (
              <IcSystemUpload
                width={16}
                height={16}
                color={BMComponents.BMStyleIconInkAction}
                accentColor={BMComponents.BMStyleIconInkAction}
              />
            )}
          </BMView>

          {!shownText() ? (
            placeholder ? (
              <BMText variant="paragraphNormal" ink="BMStyleTextInkMuted">
                {placeholder}
              </BMText>
            ) : null
          ) : (
            <BMText
              numberOfLines={1}
              style={styles.text}
              variant="paragraphNormal"
            >
              {shownText()}
            </BMText>
          )}

          {isSuccess && (
            <BMText
              variant="uiNormal"
              ink="BMStyleTextInkHighlightRed"
              style={styles.delete}
              onPress={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
            >
              {cr.deleteText}
            </BMText>
          )}
        </BMView>
        {!!caption && !errorMessage && (
          <BMText
            variant="uiSmall"
            ink="BMStyleTextInkSecondary"
            style={styles.separator}
          >
            {caption}
          </BMText>
        )}
        {!!errorMessage && (
          <BMText
            style={styles.separator}
            variant="uiSmall"
            ink="BMUiInputInkError"
          >
            {errorMessage}
          </BMText>
        )}
      </Pressable>
      {Platform.OS === "web" &&
        createElement("input", {
          ref: inputRef,
          type: "file",
          style: { display: "none" },
          onChange: handleChange,
        })}
    </BMView>
  );
}

const createStyles = (BMComponents: BMComponentsType) =>
  StyleSheet.create({
    text: { flexShrink: 1 },
    delete: { marginLeft: "auto" },
    documentContainer: {
      borderStyle: "solid",
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorBold,
      borderRadius: BMPrimitives.BMRadius.md,
      paddingVertical: BMPrimitives.BMSize.sm,
      paddingHorizontal: BMPrimitives.BMSize.md,
    },
    separator: {
      marginTop: BMPrimitives.BMSize.xs,
    },
    htmlInput: {
      display: "none",
    },
    icon: {
      flexShrink: 0,
    },
  });
